import React from 'react';
import { styled } from '@compiled/react';
import { graphql, usePreloadedQuery, type EntryPointProps } from 'react-relay';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useActiveRightSidebarState } from '@atlassian/jira-layout-controller';
import { RightSidebar } from '@atlassian/jira-navigation-system';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type { uiVirtualAgentIntentTemplatesQuery } from '@atlassian/jira-relay/src/__generated__/uiVirtualAgentIntentTemplatesQuery.graphql';
import { IntentTemplateContainer } from '../controllers/intent-template-store';
import { SidebarContent } from './content';

type Queries = { intentTemplateQueryReference: uiVirtualAgentIntentTemplatesQuery };

export default function CreateIntentTemplatesSideBar({
	queries: { intentTemplateQueryReference },
}: EntryPointProps<Queries, {}, {}, {}>) {
	const projectKey = useProjectContext().data?.projectKey;

	const intentTemplatesData = usePreloadedQuery<uiVirtualAgentIntentTemplatesQuery>(
		graphql`
			query uiVirtualAgentIntentTemplatesQuery(
				$cloudId: ID!
				$key: String!
				$isGateIntentTemplatesBehindAiToggleEnabled: Boolean!
				$isDiscoveredTemplatesTimestampEnabled: Boolean!
			) @preloadable {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $key) @required(action: THROW) {
						...content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplatesDataRef
							@arguments(
								isDiscoveredTemplatesTimestampEnabled: $isDiscoveredTemplatesTimestampEnabled
							)
					}
				}
				virtualAgent @optIn(to: ["VirtualAgent"]) @required(action: THROW) {
					virtualAgentEntitlements(cloudId: $cloudId) @required(action: THROW) {
						...content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_virtualAgentFeaturesRef
							@arguments(
								isGateIntentTemplatesBehindAiToggleEnabled: $isGateIntentTemplatesBehindAiToggleEnabled
							)
					}
				}
			}
		`,
		intentTemplateQueryReference,
	);

	const intentTemplatesDataRef = intentTemplatesData.jira.jiraProjectByKey;
	const virtualAgentFeaturesRef = intentTemplatesData.virtualAgent.virtualAgentEntitlements;

	const rightSidebarCurrentState = useActiveRightSidebarState();

	return (
		<RightSidebar
			testId="servicedesk-settings-virtual-agent-create-intent-templates-sidebar.ui.right-sidebar"
			width={
				rightSidebarCurrentState &&
				!rightSidebarCurrentState.isCollapsed &&
				!rightSidebarCurrentState.isMinimised
					? rightSidebarCurrentState.width
					: 0
			}
			isFixed
		>
			<SideBarWrapper>
				<IntentTemplateContainer scope={`intent_template_${projectKey}`}>
					<SidebarContent
						intentTemplatesDataRef={intentTemplatesDataRef}
						virtualAgentFeaturesRef={virtualAgentFeaturesRef}
					/>
				</IntentTemplateContainer>
			</SideBarWrapper>
		</RightSidebar>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SideBarWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderLeft: `2px solid ${token('color.border', colors.N30A)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.overlay', colors.N0),
	boxSizing: 'border-box',
	rowGap: token('space.100', '8px'),
	height: '100%',
	overflowY: 'auto',
});
